import React from "react";
import QuestionRow from "./QuestionRow";

const TABLE_HEADERS = ["Questionnaires", "Description", "Sub Dimension", "Response", "Comment"];

// ── Report Summary View ────────────────────────────────────────────────────
const ReportSummary = ({ apiSections, answers }) => {
  let yes = 0, no = 0, partial = 0, total = 0;

  apiSections
    .filter((sec) => sec.section !== "Report")
    .forEach((sec) => {
      (answers[sec.section] || []).forEach((a) => {
        total++;
        if (a.response === "yes")          yes++;
        else if (a.response === "no")      no++;
        else if (a.response === "partial") partial++;
      });
    });

  const pct = (n) => (total === 0 ? 0 : Math.round((n / total) * 100));

  const stats = [
    { label: "Yes",     value: yes,     pct: pct(yes),     color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
    { label: "No",      value: no,      pct: pct(no),      color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
    { label: "Partial", value: partial, pct: pct(partial), color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px 100px", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <div style={{ fontSize: "22px", fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>Assessment Summary</div>
      <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "36px" }}>Here's a breakdown of your responses across all sections</div>
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center", marginBottom: "40px", width: "100%", maxWidth: "720px" }}>
        {stats.map(({ label, value, pct: p, color, bg, border }) => (
          <div key={label} style={{ flex: "1 1 180px", background: bg, border: `1.5px solid ${border}`, borderRadius: "14px", padding: "28px 24px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: "48px", fontWeight: "800", color, lineHeight: 1 }}>{p}%</div>
            <div style={{ fontSize: "14px", fontWeight: "700", color, marginTop: "8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
            <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>{value} of {total} questions</div>
            <div style={{ marginTop: "14px", height: "6px", borderRadius: "99px", background: border, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${p}%`, background: color, borderRadius: "99px" }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ width: "100%", maxWidth: "480px", height: "1px", background: "#e2e8f0", marginBottom: "36px" }} />
      <div style={{ background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)", border: "1.5px solid #bfdbfe", borderRadius: "16px", padding: "32px 40px", textAlign: "center", maxWidth: "520px", width: "100%" }}>
        <div style={{ fontSize: "36px", marginBottom: "12px" }}>🎉</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", marginBottom: "8px" }}>Thank You!</div>
        <p style={{ fontSize: "13px", color: "#475569", lineHeight: 1.7, margin: 0 }}>
          Your assessment has been completed. Please use the <strong>Save as Draft</strong> or <strong>Submit</strong> button below to save your responses.
        </p>
      </div>
    </div>
  );
};

// ── Main QuestionTable ─────────────────────────────────────────────────────
const QuestionTable = ({
  apiSections,       // [{ section, questions: [{ id, question, description, subDimension }] }]
  sections,
  currentSectionIdx,
  reportIdx,
  answers,
  reportUnlocked,
  completion,
  onAnswer,
}) => {
  const sectionName = sections[currentSectionIdx];
  const isReport    = currentSectionIdx === reportIdx && reportIdx !== -1;
  const isLocked    = isReport && !reportUnlocked;

  if (isLocked) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "64px", marginBottom: "16px", filter: "grayscale(1)" }}>🔒</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: "#333", marginBottom: "8px" }}>Report Section is Locked</div>
        <p style={{ fontSize: "13px", color: "#888", maxWidth: "380px", marginBottom: "20px" }}>
          Answer <strong>all questions</strong> in every other section to unlock the Report.
        </p>
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "8px", padding: "10px 22px", fontSize: "13px", color: "#92400e" }}>
          Current completion: <strong>{completion}%</strong>
        </div>
      </div>
    );
  }

  if (isReport) {
    return <ReportSummary apiSections={apiSections} answers={answers} />;
  }

  // Find the current section's questions from apiSections
  const currentSection   = apiSections.find((s) => s.section === sectionName);
  const sectionQuestions = currentSection?.questions || [];
  const sectionAnswers   = answers[sectionName] || [];

  return (
    <div style={{ padding: "8px 20px 100px", overflowX: "auto" }}>
      <table style={{ width: "90%", margin: "0 auto",  borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #ddd" }}>
            {TABLE_HEADERS.map((h) => (
              <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: "700", color: "#222", background: "white", whiteSpace: "nowrap" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sectionQuestions.map((item, qi) => (
            <QuestionRow
              key={item.id || qi}
              item={{
                q:    item.question,
                desc: item.description,
                sub:  item.subDimension,
              }}
              answer={sectionAnswers[qi] || { response: null, comment: "" }}
              rowKey={`q-${currentSectionIdx}-${qi}`}
              onResponse={(val) => onAnswer(sectionName, qi, "response", val)}
              onComment={(val)  => onAnswer(sectionName, qi, "comment",  val)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default QuestionTable;